import type { EquipamentoResumo } from '../../../services';
import { normalizarPatrimonio } from '../../../models/equipamento.utils';
import { CATEGORIAS_ATIVO_ABA, categoriaDoTipo } from '../../EquipamentosListPage/components/Ativos/ativos.definitions';
import { descreverEquipamentoResumo } from '../../../utils/descreverEquipamentoResumo';

interface ListaEquipamentosColaboradorProps {
  equipamentos: EquipamentoResumo[];
  /** Com seleção (devolução parcial) = caixinhas; sem = só a lista (ex.: desligamento, vai tudo). */
  selecionados?: Set<string>;
  onToggle?: (id: string) => void;
}

/** Equipamentos do colaborador agrupados como as abas do Inventário (Computadores,
 * Monitores, Chips, Cartões de acesso...), na mesma ordem — pra não ficar tudo misturado. */
export default function ListaEquipamentosColaborador({ equipamentos, selecionados, onToggle }: ListaEquipamentosColaboradorProps) {
  const grupos = [...CATEGORIAS_ATIVO_ABA, { id: 'outros' as const, label: 'Outros', tipos: [] as string[] }]
    .map((categoria) => ({
      ...categoria,
      itens: equipamentos
        .filter((e) => categoriaDoTipo(e.tipo) === categoria.id)
        .sort((a, b) =>
          (normalizarPatrimonio(a.patrimonio) ?? '').localeCompare(normalizarPatrimonio(b.patrimonio) ?? '', 'pt-BR', { numeric: true })
        ),
    }))
    .filter((grupo) => grupo.itens.length > 0);

  return (
    <div className="mt-2 space-y-3">
      {grupos.map((grupo) => (
        <section key={grupo.id} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <header className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">{grupo.label}</p>
            <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-gray-200">
              {grupo.itens.length}
            </span>
          </header>
          <div className="divide-y divide-gray-100">
            {grupo.itens.map((equipamento) => {
              const { tipo, detalhe } = descreverEquipamentoResumo(equipamento);
              // Dentro do card a categoria já está no título; o tipo aparece só quando diz
              // algo a mais (ex.: Macbook × Notebook em "Computadores").
              const texto = (
                <span className="min-w-0">
                  <span className="font-medium text-gray-900">{detalhe}</span>
                  {grupo.tipos.length !== 1 && <span className="ml-1 text-xs text-gray-500">({tipo})</span>}
                </span>
              );
              return selecionados && onToggle ? (
                <label key={equipamento.id} className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    checked={selecionados.has(equipamento.id)}
                    onChange={() => onToggle(equipamento.id)}
                  />
                  {texto}
                </label>
              ) : (
                <p key={equipamento.id} className="px-3 py-2 text-sm text-gray-700">
                  {texto}
                </p>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
